import { useResizeObserver } from "@mantine/hooks";
import { useState, useEffect, useRef } from "react";
import { SessionResults } from "./BilanResultTabs";

type MaillageType = number[][];

function getColorForDifference(diff: number): string {
  const maxDiff = 3;
  const intensity = Math.min(Math.abs(diff), maxDiff);

  // Nuances de vert pour les positives (du plus clair au plus foncé)
  const greenShades = ["#C2F0C2", "#8DEA8D", "#4AD14A"];
  // Nuances de rouge pour les négatives
  const redShades = ["#F0C2C2", "#EA8D8D", "#D14A4A"];

  if (diff > 0) {
    return greenShades[intensity - 1];
  } else if (diff < 0) {
    return redShades[intensity - 1];
  } else {
    return "#EEEEEE"; // Neutre
  }
}

const convertPositionToIndices = (position: string) => {
  const letter = position.charAt(0);
  const number = parseInt(position.slice(1), 10);
  const col = letter.charCodeAt(0) - 65; // A=0, B=1...
  const row = number - 1;
  return { row, col };
};

// Fonction pour transformer un JSON de bilan en maillage 12x16
const parseBilanToMaillage = (bilanData: {
  resultsBilan: string;
}): MaillageType => {
  const newMaillage = [...Array(12)].map(() => Array(16).fill(0));
  if (
    bilanData &&
    bilanData.resultsBilan &&
    bilanData.resultsBilan !== "null"
  ) {
    try {
      const jsonResults = JSON.parse(bilanData.resultsBilan);
      if (jsonResults && Array.isArray(jsonResults)) {
        if (jsonResults[0]?.reactionTime !== undefined) {
          // Cas avec reactionTime
          jsonResults.forEach(
            (result: { position: string; reactionTime: string | number }) => {
              if (result.reactionTime !== "Trop long") {
                const { row, col } = convertPositionToIndices(result.position);
                newMaillage[row][col] += 1;
              }
            }
          );
        } else if (jsonResults[0]?.successCount !== undefined) {
          // Cas avec successCount
          jsonResults.forEach(
            (result: { position: string; successCount: number }) => {
              const { row, col } = convertPositionToIndices(result.position);
              newMaillage[row][col] = result.successCount;
            }
          );
        }
      }
    } catch (error) {
      console.error("Failed to parse JSON data:", error);
    }
  }
  return newMaillage;
};

const CanvasGridDifference = ({
  width,
  height,
  diffMaillage,
  removedRows,
  removedColumns,
}: {
  width: number;
  height: number;
  diffMaillage: MaillageType;
  /** The merged/aggregated set of removed rows for the combined data A + B */
  removedRows: number[];
  /** The merged/aggregated set of removed columns for the combined data A + B */
  removedColumns: number[];
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        const cellHeight = height / 12;
        const cellWidth = cellHeight * (5.5 / 4); // Width based on height with ratio 5.5/4

        context.clearRect(0, 0, width, height);

        diffMaillage.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            // 1) Check if row/col is removed in either A or B
            const isRemovedRow = removedRows.includes(rowIndex);
            const isRemovedCol = removedColumns.includes(colIndex);

            if (isRemovedRow || isRemovedCol) {
              // => Pink background
              context.fillStyle = "#006599";
              context.fillRect(
                colIndex * cellWidth,
                rowIndex * cellHeight,
                cellWidth,
                cellHeight
              );
              context.strokeStyle = "black";
              context.strokeRect(
                colIndex * cellWidth,
                rowIndex * cellHeight,
                cellWidth,
                cellHeight
              );

              // Pink text
              context.fillStyle = "#006599";
              context.font = "bold 16px Arial";
              const text = String(cell);
              const textWidth = context.measureText(text).width;
              const textX = colIndex * cellWidth + (cellWidth - textWidth) / 2;
              const textY = rowIndex * cellHeight + cellHeight / 2 + 6;
              context.fillText(text, textX, textY);
            } else {
              // Choix des couleurs en fonction de la différence
              // Par exemple :
              // > 0 : plus le nombre est grand, plus c'est vert foncé
              // < 0 : plus le nombre est négatif, plus c'est rouge foncé
              // = 0 : gris neutre
              const color = getColorForDifference(cell);

              context.fillStyle = color;
              context.fillRect(
                colIndex * cellWidth,
                rowIndex * cellHeight,
                cellWidth,
                cellHeight
              );

              context.strokeStyle = "black";
              context.strokeRect(
                colIndex * cellWidth,
                rowIndex * cellHeight,
                cellWidth,
                cellHeight
              );

              // Affichage de la valeur
              context.fillStyle = "black";
              context.font = "bold 16px Arial";
              const text = String(cell);
              const textWidth = context.measureText(text).width;
              const textX = colIndex * cellWidth + (cellWidth - textWidth) / 2;
              const textY = rowIndex * cellHeight + cellHeight / 2 + 6;
              context.fillText(text, textX, textY);
            }
          });
        });

        // Dessin de la croix centrale pour repère
        const totalGridWidth = 16 * cellWidth;
        const centerX = totalGridWidth / 2;
        const centerY = height / 2;
        const crossLength = Math.min(cellWidth, cellHeight) * 0.5;

        context.beginPath();
        context.strokeStyle = "red";
        context.lineWidth = 2;
        context.moveTo(centerX - crossLength / 2, centerY);
        context.lineTo(centerX + crossLength / 2, centerY);
        context.stroke();

        context.beginPath();
        context.moveTo(centerX, centerY - crossLength / 2);
        context.lineTo(centerX, centerY + crossLength / 2);
        context.stroke();
      }
    }
  }, [diffMaillage, width, height, removedColumns, removedRows]);

  return (
    <canvas
      ref={canvasRef}
      width={16 * (height / 12) * (5.5 / 4)}
      height={height}
    />
  );
};

export const ResultsBilanComparatifDisplay = ({
  bilanData1,
  bilanData2,
}: {
  bilanData1: SessionResults;
  bilanData2: SessionResults;
}) => {
  const [ref, rect] = useResizeObserver();

  const [mergedRemovedRows, setMergedRemovedRows] = useState<number[]>([]);
  const [mergedRemovedColumns, setMergedRemovedColumns] = useState<number[]>(
    []
  );

  const [diffMaillage, setDiffMaillage] = useState(
    [...Array(12)].map(() => Array(16).fill(0))
  );

  useEffect(() => {
    const maillage1 = parseBilanToMaillage(bilanData1);
    const maillage2 = parseBilanToMaillage(bilanData2);

    // Calcul de la différence
    const newDiff = maillage1.map((row, i) =>
      row.map((val, j) => maillage2[i][j] - val)
    );
    setDiffMaillage(newDiff);

    // Fusion des removedRows et removedColumns
    const mergedRemovedRows = Array.from(
      new Set([...bilanData1.removedRows, ...bilanData2.removedRows])
    );
    setMergedRemovedRows(mergedRemovedRows);

    const mergedRemovedColumns = Array.from(
      new Set([...bilanData1.removedColumns, ...bilanData2.removedColumns])
    );
    setMergedRemovedColumns(mergedRemovedColumns);
  }, [bilanData1, bilanData2]);

  const maxWidth = rect.width > 1400 ? 1400 : rect.width;

  return (
    <div ref={ref}>
      <CanvasGridDifference
        width={maxWidth}
        height={maxWidth * 0.54}
        diffMaillage={diffMaillage}
        removedRows={mergedRemovedRows}
        removedColumns={mergedRemovedColumns}
      />
    </div>
  );
};
